import { t, useLanguage } from "@/lib/i18n";
import ReligionSelect from "@/components/ReligionSelect";
import CasteGotraFields from "@/components/CasteGotraFields";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TreePine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import GoogleSignIn from "@/components/GoogleSignIn";

export default function Register() {
  useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [religion, setReligion] = useState("Hindu");
  const [caste, setCaste] = useState("");
  const [castes, setCastes] = useState([]);
  const [gotra, setGotra] = useState("");
  const [gotras, setGotras] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/gotras"), api.get("/castes")]).then(([g, c]) => { setGotras(g.data); setCastes(c.data); })
      .catch(() => setError("Could not load Gotra list. Please reload."));
  }, []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match. Please confirm your password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, phone_number: phoneNumber, password, religion, caste, gotra });
      login(data);
      if (data.verification_email_sent) toast.success(t("Check your email to verify your account."));
      else toast.error(t("Account created, but the verification email could not be sent. Please request a new link."));
      navigate("/verify-email");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#2C1E16] text-amber-50 p-12 relative overflow-hidden">
        <div className="parchment-grain absolute inset-0 opacity-40" />
        <Link to="/" className="relative flex items-center gap-2 font-serif text-2xl font-bold" data-testid="register-logo-link">
          {t(" VanshVriksham")}</Link>
        <div className="relative">
          <h2 className="font-serif text-4xl font-bold leading-tight">{t("Plant your family tree today.")}</h2>
          <p className="mt-4 text-amber-100/70 max-w-md">{t("Add ancestors, link generations, and explore your Gotra community tree.")}</p>
          <div className="mt-8 flex items-center gap-2 text-sm text-[#D4A359]">
            <ShieldCheck size={18} />{t(" Members are visible to signed-in users; editing requires permission")}</div>
        </div>
        <div className="relative text-xs font-mono text-amber-100/40">{t("© 2026 VanshVriksham Heritage Vault")}</div>
      </div>

      <div className="flex items-center justify-center p-5 sm:p-8 bg-[#FAF7F2]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="register-form" autoComplete="off">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-800/80">{t("Begin your legacy")}</p>
            <h1 className="font-serif text-4xl font-bold mt-1">{t("Create account")}</h1>
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="register-error">
              {t(error)}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">{t("Full name ")}<span className="text-red-600" aria-hidden="true">*</span></Label>
            <Input id="name" name="full_name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" placeholder={t("Your name")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone-number">{t("Mobile number ")}<span className="text-stone-500 text-xs">{t("(optional)")}</span></Label>
            <Input id="phone-number" name="mobile_number" autoComplete="tel" type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} data-testid="register-phone-input" placeholder={t("Your mobile number")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("Email ")}<span className="text-red-600" aria-hidden="true">*</span></Label>
            <Input id="email" name="register_email" autoComplete="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" placeholder="you@family.com" />
            <p className="text-xs text-stone-500">{t("Verify your email to access your account and add family members.")}</p>
          </div>
          <ReligionSelect id="register-religion" value={religion} onChange={(value) => { setReligion(value); setCaste(""); setGotra(""); }} />
          <CasteGotraFields religion={religion} caste={caste} gotra={gotra} castes={castes} gotras={gotras} prefix="register"
            onChange={(values) => { setCaste(values.caste); setGotra(values.gotra); }} />
          <div className="space-y-2">
            <Label htmlFor="password">{t("Password ")}<span className="text-red-600" aria-hidden="true">*</span></Label>
            <Input id="password" name="new_password" autoComplete="new-password" type="password" minLength={8} maxLength={72} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password-input" placeholder={t("At least 8 characters")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t("Confirm password ")}<span className="text-red-600" aria-hidden="true">*</span></Label>
            <Input id="confirm-password" name="confirm_password" autoComplete="new-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="register-confirm-password-input" placeholder={t("Re-enter your password")} />
          </div>
          <Button type="submit" disabled={loading} className="w-full gold-btn text-[#2C1E16] font-semibold border-0 h-11" data-testid="register-submit-button">
            {loading ? t("Creating…") : t("Create account")}
          </Button>
          <GoogleSignIn onSuccess={(data) => {
            login(data);
            navigate(data.user.email_verified ? "/app" : "/verify-email");
          }} />
          <p className="text-sm text-stone-600 text-center">{t("Already have an account?")}{" "}
            <Link to="/login" className="text-amber-800 font-semibold hover:underline" data-testid="register-to-login-link">{t("Sign in")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
